import React, { useEffect } from 'react';

import { track } from '@betterment/js-runtime';

import MessageBanner from './message-banner';
import MessageGuidanceBox from './message-guidance-box';

import { Message } from '../../../graphql/schema';
import { UserCookieState, useMessageVisibility } from '../../hooks/useMessageVisibility';

export interface MessageBoxProps {
  messages: Message[];
  children?: React.ReactNode;
  location: string;
  userCookieState?: UserCookieState;
}

const MessageBox: React.FC<MessageBoxProps> & {
  Before: React.FC<{ children?: React.ReactNode }>;
  After: React.FC<{ children?: React.ReactNode }>;
} = ({ messages, location, userCookieState: initialUserCookieState, children }) => {
  const message = messages[0];
  const messageId = message?.id || '';
  const isGoalAchievedMessage = message?.trackingName === 'goal_achieved';
  const userCookieState = isGoalAchievedMessage ? initialUserCookieState : undefined;

  const { isVisible, handleCloseMessage } = useMessageVisibility({
    messageId,
    userCookieState,
  });

  useEffect(() => {
    if (message && message.trackingName && isVisible) {
      track('ElementViewed', { name: message.trackingName, location });
    }
  }, [isVisible, location, message]);

  if (!isVisible || !message) return null;

  const beforeChildren = React.Children.toArray(children).find(
    (child): child is React.ReactElement => React.isValidElement(child) && child.type === MessageBox.Before
  );

  const afterChildren = React.Children.toArray(children).find(
    (child): child is React.ReactElement => React.isValidElement(child) && child.type === MessageBox.After
  );

  const eventProperties = message.trackingName ? { name: message.trackingName, location } : undefined;

  return (
    <>
      {beforeChildren}

      {message.style === 'ADVICE' ? (
        <MessageGuidanceBox message={message} handleCloseMessage={handleCloseMessage} />
      ) : (
        <MessageBanner message={message} handleCloseMessage={handleCloseMessage} eventProperties={eventProperties} />
      )}

      {afterChildren}
    </>
  );
};

MessageBox.Before = ({ children }) => <>{children}</>;
MessageBox.After = ({ children }) => <>{children}</>;

export default MessageBox;
